import { IntroScreen as TLIntroScreen } from '@treedis/tour-loader';
import { useCallback, useEffect, useState } from 'react';
import { getVersion } from '@/components/util';
import { useLanguage } from '@/hooks/index';
import { useLoadingProgress } from '@/hooks/useLoadingProgress';
import color from '@/utils/muiColor';

type Props = {
  rtl?: boolean;
  tour: any;
  initialDuplicates: any;
  introScreen: any;
  preventSubmit?: boolean;
  whiteLabelData?: {
    isWLUser: boolean;
    primaryColor: string;
    secondaryColor: string;
    logo: string;
    website: string;
  };
  translation: any;
  showCollectLeadsModal?: boolean;
  inputs: {
    name: boolean;
    email: boolean;
    phone: boolean;
  };
  checkbox: {
    legal1: string;
    legal2: string;
  };
  onStart: (data: any) => void;
  onFinish: () => void;
  preventSubmitCallback: () => void;
};

export function IntroScreen({
  rtl,
  introScreen,
  tour,
  checkbox,
  whiteLabelData,
  translation,
  showCollectLeadsModal,
  initialDuplicates,
  inputs,
  preventSubmit,
  onStart,
  onFinish,
  preventSubmitCallback,
}: Props) {
  const { loadingProgress } = useLoadingProgress();
  const { languageOptions, defaultLanguage, handleChange } = useLanguage({ initialDuplicates, tourSlug: tour?.slug });
  const [isStarted, setStarted] = useState(false);

  const handleSubmit = useCallback(
    async (data: Record<string, string>) => {
      setStarted(true);
      onStart(data);
    },
    [onStart],
  );

  useEffect(() => {
    if (showCollectLeadsModal) {
      // sendToGA('event', {
      //   eventCategory: 'Lead',
      //   eventAction: 'open',
      //   eventLabel: 'entry',
      // });
    }
  }, [showCollectLeadsModal]);

  useEffect(() => {
    if (loadingProgress === 100 && isStarted) {
      onFinish();
    }
  }, [loadingProgress, onFinish, isStarted]);

  const getBackgroundVideo = () => {
    if (introScreen?.videoLink) return introScreen?.videoLink;
    if (introScreen?.uploadedVideoUrl) return `${process.env.REACT_APP_AWS}/${introScreen?.uploadedVideoUrl}${getVersion(introScreen?.updatedAt)}`;
    return '';
  };
  return (
    <TLIntroScreen
      rtl={rtl}
      hideHelpButton={introScreen?.hideHelpButton}
      title={tour.showTitle ? introScreen?.title || tour.title || '' : ''}
      description={introScreen?.description || tour.address || ''}
      backgroundVideo={getBackgroundVideo()}
      loading={isStarted ? loadingProgress !== 100 : undefined}
      loadingProgress={loadingProgress as number}
      termFile={translation.termFile ? `${process.env.REACT_APP_AWS}/tours/${tour.id}/termFile.pdf` : undefined}
      isTextShadowed={introScreen?.isTextShadowed}
      enableTips={introScreen?.enableTips}
      darkBackground={introScreen?.darkBackground}
      blurBackground={introScreen?.blurBackground}
      // Client logo for public is shown in the BaseTour component
      client={{
        show: !!tour?.client && introScreen?.enableLogo,
        logo: tour?.clientId ? `${process.env.REACT_APP_AWS}/clients/${tour.clientId}/logo.png${getVersion(tour?.client?.updatedAt || new Date())}` : '',
        horizontalLogo: !!tour?.client?.widthLogo,
        // eslint-disable-next-line no-nested-ternary
        website: tour?.client?.website ? (tour?.client.website?.includes('https://') ? tour?.client.website : `https://${tour?.client.website}`) : '',
      }}
      backgroundImage={
        introScreen?.backgroundImage
          ? `${process.env.REACT_APP_AWS}/tours/${tour.id}/images/${introScreen.backgroundImage}${getVersion(tour.updatedAt)}`
          : `${process.env.REACT_APP_AWS}/tours/${tour.id}/images/${tour.mainImage}${getVersion(tour.updatedAt)}`
      }
      language={{
        options: languageOptions || [],
        default: defaultLanguage,
        onChange: handleChange,
      }}
      button={{
        label: tour?.translation?.introScreenButtonLabel,
        showPlayButton: introScreen?.showPlayButton,
        primaryColor: introScreen?.buttonPrimaryColor,
        secondaryColor: introScreen?.buttonSecondaryColor,
      }}
      whiteLabel={{
        show: Boolean(whiteLabelData?.logo?.length),
        isWLUser: whiteLabelData?.isWLUser,
        logo: whiteLabelData?.logo,
        website: whiteLabelData?.website,
        ...(whiteLabelData?.primaryColor
          ? {
              primaryColor: {
                light: color.light(whiteLabelData?.primaryColor),
                main: whiteLabelData?.primaryColor,
                dark: color.dark(whiteLabelData?.primaryColor),
              },
            }
          : {}),
        ...(whiteLabelData?.secondaryColor
          ? {
              secondaryColor: {
                light: color.light(whiteLabelData?.secondaryColor),
                main: whiteLabelData?.secondaryColor,
                dark: color.dark(whiteLabelData?.secondaryColor),
              },
              textColor: {
                secondary: color.lighter(whiteLabelData?.secondaryColor),
                primary: whiteLabelData?.secondaryColor,
                disabled: color.light(whiteLabelData?.secondaryColor),
              },
            }
          : {}),
      }}
      inputs={inputs}
      onSubmit={handleSubmit}
      preventSubmit={preventSubmit}
      preventSubmitCallback={preventSubmitCallback}
      translation={translation}
      checkbox={checkbox}
    />
  );
}
